/*
 * Deferred half of the shipping-banner -> announcement-bar sync.
 *
 * The pre-paint inline script in theme.liquid already injects the cached
 * message before the carousel initializes, so on repeat visits the bar
 * is present from first paint with no flicker. This deferred script
 * handles the rest:
 *   - cache MISS (first visit / new location): wait for the live banner,
 *     inject the cell, and re-init the carousel (it has already
 *     initialized by the time we run);
 *   - live banner load: write the value to the cache for next time and
 *     update the cell text in place if it changed (self-healing).
 *
 * Cell building lives in shipping-banner-cache.js
 * (ShippingBannerCache.injectAnnouncementCell) so the inline apply and
 * this script share one implementation.
 */
(function () {
  var SOURCE_SELECTOR = '#FreeShippingBanner';
  var MESSAGE_LIST_SELECTOR = 'announcement-bar .announcement-bar__message-list';
  var READY_ATTR = 'data-shipping-banner-ready';
  var FAILSAFE_REVEAL_MS = 4000;

  function cache() {
    return window.ShippingBannerCache || null;
  }

  function markReady() {
    if (!document.documentElement.hasAttribute(READY_ATTR)) {
      document.documentElement.setAttribute(READY_ATTR, '');
    }
  }

  // Detach/re-attach the announcement-bar element so its carousel rebuilds
  // and picks up a newly inserted cell. Only needed when we insert a cell
  // AFTER the carousel has initialized (the cache-miss path).
  function reinitAnnouncementBar() {
    var messageList = document.querySelector(MESSAGE_LIST_SELECTOR);
    if (!messageList) return;
    var bar = messageList.closest('announcement-bar');
    if (!bar || !bar.parentNode) return;
    var parent = bar.parentNode;
    var next = bar.nextSibling;
    parent.removeChild(bar);
    parent.insertBefore(bar, next);
  }

  function applyToCarousel(text) {
    if (!cache()) return false;
    var result = cache().injectAnnouncementCell(text);
    if (!result) return false;
    // 'created' means we just added the cell. If the early inline script
    // already added it (cache hit), we get 'updated' here and skip the
    // re-init, so there is no flicker.
    if (result === 'created') reinitAnnouncementBar();
    markReady();
    return true;
  }

  function syncFromLiveBanner() {
    var source = document.querySelector(SOURCE_SELECTOR);
    if (!source) return false;

    var text = (source.textContent || '').trim();
    if (!text) return false;

    applyToCarousel(text);

    if (source.style.display !== 'none') {
      source.style.display = 'none';
      source.setAttribute('aria-hidden', 'true');
    }

    if (cache()) cache().set(text);
    return true;
  }

  function watchSourceForChanges() {
    var source = document.querySelector(SOURCE_SELECTOR);
    if (!source) return;
    var srcObserver = new MutationObserver(syncFromLiveBanner);
    srcObserver.observe(source, { childList: true, characterData: true, subtree: true });
  }

  function init() {
    // The inline pre-paint script already applied the cached value if
    // there was one. We still try the live banner to persist/refresh it.
    if (syncFromLiveBanner()) {
      watchSourceForChanges();
      return;
    }

    // Cache miss and banner not present yet - watch for it.
    var observer = new MutationObserver(function () {
      if (syncFromLiveBanner()) {
        observer.disconnect();
        watchSourceForChanges();
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Reveal anyway if no banner ever shows (theme default copy stays).
    setTimeout(function () {
      observer.disconnect();
      markReady();
    }, FAILSAFE_REVEAL_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
